import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { z } from "zod";
import { format } from "date-fns";
import moment from "moment";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];

const imageSchema = z
  .string()
  .min(5)
  .or(
    z
      .instanceof(File)
      .refine((file) => file.size <= MAX_FILE_SIZE, {
        message: "Image must be less than 5MB",
      })
      .refine((file) => ACCEPTED_IMAGE_TYPES.includes(file.type), {
        message: "Only .jpg, .jpeg, .png are supported",
      })
  );

export const myProfileFormSchema = () =>
  z.object({
    name: z
      .string()
      .trim()
      .min(3, { message: "Name must be at least 3 characters" })
      .regex(/^[a-zA-Z\s]*$/, {
        message: "Name must not contain special characters",
      }),
    email: z
      .string()
      .trim()
      .email({ message: "Invalid email address" })
      .refine((email) => !/\s/.test(email), {
        message: "Email address should not contain spaces",
      }),
    mobile: z
      .string()
      .regex(/^\d{10}$/, { message: "Mobile number must be 10 digits" }),
    dateOfBirth: z.string().refine((val) => !isNaN(Date.parse(val)), {
      message: "Invalid date format",
    }),
    bloodGroup: z.preprocess(
      (val) => (val === "" ? undefined : val),
      z.enum(["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"], {
        errorMap: () => ({ message: "Please select your blood group" }),
      })
    ),
    gender: z.preprocess(
      (val) => (val === "" ? undefined : val),
      z.enum(["MALE", "FEMALE", "OTHER"], {
        errorMap: () => ({ message: "Please select gender" }),
      })
    ),
    jerseySize: z.preprocess(
      (val) => (val === "" ? undefined : val),
      z.enum(["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"], {
        errorMap: () => ({ message: "Please select your jersey size" }),
      })
    ),
    country: z.string().nonempty({ message: "Country is required" }),
    state: z.string().nonempty({ message: "State is required" }),
    city: z.string().nonempty({ message: "City is required" }),
    address: z.string().nonempty({ message: "Address is required" }),
    pinCode: z
      .string()
      .length(6, { message: "Pincode must be exactly 6 digits" })
      .regex(/^\d+$/, { message: "Pincode must be numeric" }),
    emergencyContactName: z
      .string()
      .nonempty({ message: "Emergency contact name is required" }),
    emergencyContactNumber: z.string().regex(/^\d{10}$/, {
      message:
        "Emergency contact number must be 10 digits and must be Indian number",
    }),
    gstNumber: z.string().optional(),
    profileImage: imageSchema.optional(), // Assuming image is optional
    aadharImage: z
      .union([
        z
          .instanceof(File)
          .refine(
            (file) =>
              file.type.startsWith("image/") || file.type === "application/pdf",
            { message: "Only images or PDF files are supported" }
          ),
        z.string().nonempty({ message: "Aadhar image or PDF is required" }), // For pre-filled files
      ])
      .refine(
        (value) => {
          if (typeof value === "string" && value.trim() === "") return false;
          return true;
        },
        { message: "Please upload your Aadhar card" }
      ),
  });
export const membersRegistrationFormSchema = () =>
  z.object({
    name: z
      .string()
      .trim()
      .min(3, { message: "Name must be at least 3 characters" }),
    email: z
      .string()
      .trim()
      .email({ message: "Invalid email address" })
      .refine((email) => !/\s/.test(email), {
        message: "Email address should not contain spaces",
      }),
    mobile: z.string().regex(/^\d{10}$/, {
      message:
        "Mobile number must be 10 digits and Mobile number must be Indian",
    }),
    dateOfBirth: z.string().refine((val) => !isNaN(Date.parse(val)), {
      message: "Invalid date format",
    }),
    bloodGroup: z.enum(["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"], {
      errorMap: () => ({ message: "Please select your blood group" }),
    }),
    gender: z.enum(["MALE", "FEMALE", "OTHER"], {
      errorMap: () => ({ message: "Please select gender" }),
    }),
    jerseySize: z.enum(["XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL"], {
      errorMap: () => ({ message: "Please select your jersey size" }),
    }),
  });

export const signupUserSchema = z.object({
  name: z
    .string()
    .trim()
    .min(3, { message: "Name must be at least 3 characters" })
    .regex(/^[a-zA-Z\s]*$/, {
      message: "Name must not contain special characters",
    }),
  email: z
    .string()
    .trim()
    .email({ message: "Invalid email address" })
    .refine((email) => !/\s/.test(email), {
      message: "Email address should not contain spaces",
    }),
  password: z
    .string()
    .min(6, { message: "Password must be at least 6 characters" }),
  mobile: z.string().regex(/^\d{10}$/, { message: "Invalid mobile number" }),
});

export const loginUserSchema = z.object({
  email: z
    .string()
    .nonempty({ message: "Please enter email id" })
    .email({ message: "Invalid email address" }),
  password: z.string().nonempty({ message: "Please enter your password" }),
});

export const contactUsSchema = z.object({
  name: z
    .string()
    .trim()
    .min(3, { message: "Name must be at least 3 characters" })
    .regex(/^[a-zA-Z\s]*$/, {
      message: "Name must not contain special characters",
    }),
  email: z
    .string()
    .trim()
    .email({ message: "Invalid email address" })
    .refine((email) => !/\s/.test(email), {
      message: "Email address should not contain spaces",
    }),
  mobile: z
    .string()
    .nonempty({ message: "Please enter mobile number" })
    .regex(/^\d{10}$/, { message: "Invalid mobile number" }),
  query: z.string().nonempty({ message: "Please enter your query" }),
});

export const setPasswordSchema = z.object({
  password: z.string().nonempty({ message: "Please enter your password" }),
  confirmPassword: z
    .string()
    .nonempty({ message: "Please confirm your password" }),
});

export const changePasswordSchema = z.object({
  oldPassword: z.string().nonempty({ message: "Please enter old password" }),
  newPassword: z.string().nonempty({ message: "Please enter new password" }),
  confirmPassword: z
    .string()
    .nonempty({ message: "Please confirm new password" }),
});

export const forgotPassword = z.object({
  email: z
    .string()
    .nonempty({ message: "Please enter email id" })
    .email({ message: "Invalid email address" }),
});

export const verifyEmailSchema = z.object({
  email: z.string().email(),
});
export const verifyMobileSchema = z.object({
  mobile: z.string(),
});
export type User = {
  name: string;
  email: string;
  mobile: string;
  profileImage: string;
  state: string;
  isBlocked: boolean;
};

export const phoneSchema = z.object({
  phone: z
    .string()
    .nonempty({ message: "Phone number is required" })
    .transform((val) => val.replace(/\s+/g, "")) // Remove spaces
    .transform((val) => val.replace(/^\+91/, "")) // Remove country code if present
    .refine((val) => /^\d{10}$/.test(val), {
      message: "Phone number must be 10 digits",
    }),
});

export const phoneSchema2 = z.object({
  phone: z
    .string()
    .nonempty({ message: "Phone number is required" })
    .transform((val) => val.replace(/\s+/g, "")) // Remove spaces
    .refine((val) => /^\+?\d{1,4}\d{10}$/.test(val), {
      message: "Phone number must include country code and be 10 digits long",
    }),
});
export type SetAuthenticated = (isAuth: boolean) => void;
export type SetUser = (user: User) => void;

// functions
export const formatDate = (isoDate: Date) => {
  return format(new Date(isoDate), "dd MMM yyyy (EEEE)");
};

export const formatMomentDate = (dateString: Date) => {
  return moment(dateString).format("MMM DD");
};

export const newUsers = [
  {
    id: "1",
    name: "John Doe",
    title: "Runner",
    date: "2024-09-21",
    eventPrice: "50",
  },
  {
    id: "2",
    name: "Jane Smith",
    title: "Runner",
    date: "2024-09-22",
    eventPrice: "60",
  },
];

export const calculateAge = function calculateAge(birthdate: string): number {
  const birthDate = moment(birthdate);
  const currentDate = moment();
  const age = currentDate.diff(birthDate, "years");
  return age;
};

export interface Member {
  id: string;
  name: string;
  email: string;
  mobile: string;
  phoneCode: string;
  birthDate: string;
  gender: string;
  jerseySize: string;
  bloodGroup: string;
  profileImage: string;
  status: "UNVERIFIED" | "VERIFIED";
  isPrimary: boolean;
  user: {
    status: "REJECTED" | "APPROVED";
    profileImage: string;
    state: string;
  };
}
